/**
 * The Altar Offering: what a finished run pays (crafting.md §2). Dungeons pay RESOURCES, never
 * finished items. This module is pure: `rollRunResources` is the per-run payout (it replaces
 * rollRunLoot's item branch; gems are unchanged), and the daily / bracket / weekly / vault
 * helpers compute their lines from forge state plus the CALLER's local date string. Nothing here
 * reads a clock. profile.applyRunResult folds the results into the profile.
 */
import type { ClassId, DungeonId, Rng, WorldFinds } from '@keystone/sim';
import { getContentBundle } from '@keystone/content';
import {
  BLESSED_CAP,
  BRACKET_CACHE,
  DAILY_BLESSED,
  DAILY_SIGIL_BANK,
  EYE_EXTRA_CHANCE,
  VAULT_ALTAR_CACHE,
  VAULT_SCALE_CACHE,
  WEEKLY_RITUAL_RUNS,
  WEEKLY_RITUAL_SCALES,
  gradeForKey,
  gradeIndex,
} from './forge.constants';
import { dayNumber, favoredSlot, isLocalDate, isoWeek } from './forge.dates';
import { addBundles } from './forge.wallet';
import { rollRunGems } from './loot';
import { RANK_TIER_ORDER, SCALE_GRADES } from './types';
import type { ForgeState, Gem, ItemSlot, RankTier, ResourceBundle, ScaleGrade, VaultOffer, VaultOption } from './types';

const BUNDLE = getContentBundle();

/** Bosses a completed run of this dungeon killed (its required encounters; 3 for the MVP). */
export function dungeonBossCount(dungeonId: DungeonId): number {
  const d = BUNDLE.dungeons.find((x) => x.id === dungeonId);
  return d ? d.requiredEncounters.length : 3;
}

const atLeast = (tier: RankTier, min: RankTier): boolean => RANK_TIER_ORDER.indexOf(tier) >= RANK_TIER_ORDER.indexOf(min);

export interface RunResourceContext {
  level: number;
  completed: boolean;
  timed: boolean;
  chest: 0 | 1 | 2 | 3;
  /** Bosses killed (default: every required boss of a completed run, 0 otherwise). */
  bosses: number;
  /** The final boss died (completed runs always). */
  finalBoss: boolean;
  /** Rank tier BEFORE the run (Eye fragments from Champion). */
  tier: RankTier;
  /** Gem recipients (party size; gem i belongs to recipient i). */
  recipients: number;
  /** Gems are unlocked for this profile. */
  gemsUnlocked: boolean;
}

export interface RunResources {
  /** Grade of the run's Coil Scales. */
  grade: ScaleGrade;
  /** The human's completion + chest + boss lines (no daily/bracket/world). */
  base: ResourceBundle;
  /** Fangsteel + Scales each companion satchel gets (the base payout's metal). */
  satchel: ResourceBundle;
  gems: Gem[];
}

/**
 * The per-run Offering (crafting.md §2.1). Abandoned runs pay nothing (kills still pay gold/XP
 * via rewards.ts). RNG draw order (save contract): gems exactly as rollRunLoot's gem branch, then
 * the final boss's extra Eye fragment chance (Paragon+, completed runs only).
 */
export function rollRunResources(ctx: RunResourceContext, rng: Rng): RunResources {
  const gems = rollRunGems(ctx.level, ctx.timed, ctx.tier, ctx.recipients, ctx.gemsUnlocked, rng);
  const L = Math.max(0, ctx.level);
  if (!ctx.completed) return { grade: gradeForKey(L), base: {}, satchel: {}, gems };
  const bosses = Math.max(0, ctx.bosses);
  let grade: ScaleGrade;
  const base: ResourceBundle = {};
  if (ctx.timed) {
    grade = gradeForKey(L);
    base.fangsteel = 15 + 2 * L + 4 * ctx.chest;
    base.scales = { [grade]: 3 + ctx.chest + bosses };
    base.venom = 1 + bosses;
    base.moltskin = 1 + (ctx.chest === 3 ? 1 : 0);
  } else {
    grade = gradeForKey(L - 1);
    base.fangsteel = 8 + L;
    base.scales = { [grade]: 1 + bosses };
    if (bosses) base.venom = bosses;
  }
  if (ctx.finalBoss && atLeast(ctx.tier, 'champion')) {
    const extra = EYE_EXTRA_CHANCE[ctx.tier] ?? 0;
    base.eyeFragments = 1 + (extra > 0 && rng.next() < extra ? 1 : 0);
  }
  const satchel: ResourceBundle = { fangsteel: base.fangsteel!, scales: { ...base.scales } };
  return { grade, base, satchel, gems };
}

/** Deterministic expectation of a run's base payout (no Eye extra roll): the "next run" hint. */
export function estimateRunResources(level: number, timed: boolean, chest: 0 | 1 | 2 | 3, tier: RankTier, dungeonId?: DungeonId): RunResources {
  const zero = { next: () => 1, int: () => 0, range: () => 0, chance: () => false, pick: () => undefined, getState: () => ({}) as never, setState: () => {} } as unknown as Rng;
  return rollRunResources(
    { level, completed: true, timed, chest, bosses: dungeonId ? dungeonBossCount(dungeonId) : 3, finalBoss: true, tier, recipients: 0, gemsUnlocked: false },
    zero,
  );
}

// ============================================================================
// Daily Offering (first completed run of a calendar day)
// ============================================================================

export interface DailyOffering {
  day: number;
  sigils: number;
  /** Altar's Favor (the run's Scales again) + Blessed Charges. */
  bonus: ResourceBundle;
  favoredSlot: ItemSlot;
}

/** Sigils a daily claim on `day` pays (banked up to DAILY_SIGIL_BANK), 0 if already claimed. */
export function dailySigils(lastClaimDay: number | null, day: number): number {
  if (lastClaimDay === null) return 1;
  if (day <= lastClaimDay) return 0;
  return Math.min(DAILY_SIGIL_BANK, day - lastClaimDay);
}

/** The Daily Offering a completed run on `localDate` would claim, or null (already claimed / bad date). */
export function dailyOffering(forge: Pick<ForgeState, 'daily'>, localDate: string | undefined, runScales: ResourceBundle['scales']): DailyOffering | null {
  if (!localDate || !isLocalDate(localDate)) return null;
  const day = dayNumber(localDate);
  const sigils = dailySigils(forge.daily.lastClaimDay, day);
  if (sigils === 0) return null;
  const bonus: ResourceBundle = { blessed: DAILY_BLESSED };
  if (runScales && Object.keys(runScales).length) bonus.scales = { ...runScales };
  return { day, sigils, bonus, favoredSlot: favoredSlot(day) };
}

/** Daily status for the hub (no claim): ready, banked Sigils, favored slot. */
export function dailyStatus(forge: Pick<ForgeState, 'daily'>, localDate: string): { ready: boolean; sigils: number; favoredSlot: ItemSlot; blessedCap: number } {
  const day = dayNumber(localDate);
  const sigils = dailySigils(forge.daily.lastClaimDay, day);
  return { ready: sigils > 0, sigils, favoredSlot: favoredSlot(day), blessedCap: BLESSED_CAP };
}

// ============================================================================
// First timed clear per dungeon per bracket
// ============================================================================

export function bracketCache(grade: ScaleGrade): ResourceBundle {
  const b: ResourceBundle = { sigils: 1, fangsteel: BRACKET_CACHE.fangsteel, scales: { [grade]: BRACKET_CACHE.scales } };
  if (gradeIndex(grade) >= gradeIndex('gilded')) b.eyeFragments = 1;
  return b;
}

/** The bracket line for a timed run, or null when this dungeon's bracket was already cleared. */
export function bracketClear(forge: Pick<ForgeState, 'brackets'>, dungeonId: DungeonId, level: number, timed: boolean): { grade: ScaleGrade; bundle: ResourceBundle } | null {
  if (!timed) return null;
  const grade = gradeForKey(level);
  if (forge.brackets[dungeonId]?.includes(grade)) return null;
  return { grade, bundle: bracketCache(grade) };
}

// ============================================================================
// Weekly Ritual (ISO week of the caller's date)
// ============================================================================

/** Weekly state after a completed run on `localDate` (resets on a new ISO week). */
export function weeklyAfterRun(w: ForgeState['weekly'], localDate: string): ForgeState['weekly'] {
  const wk = isoWeek(dayNumber(localDate));
  const cur = w.isoWeek === wk ? w : { isoWeek: wk, runs: 0, claimed: false };
  return { ...cur, runs: cur.runs + 1 };
}

/** Can the Ritual Cache be claimed on `localDate`? */
export function weeklyReady(w: ForgeState['weekly'], localDate: string): boolean {
  if (!isLocalDate(localDate)) return false;
  return w.isoWeek === isoWeek(dayNumber(localDate)) && w.runs >= WEEKLY_RITUAL_RUNS && !w.claimed;
}

export type RitualChoice = 'sigil' | 'eye';

/** Ritual Cache contents: 1 Sigil or 1 full Eye (Champion+), plus Scales of the best grade. */
export function ritualCache(choice: RitualChoice, bestGrade: ScaleGrade): ResourceBundle {
  return { ...(choice === 'sigil' ? { sigils: 1 } : { eyeFragments: 5 }), scales: { [bestGrade]: WEEKLY_RITUAL_SCALES } };
}

// ============================================================================
// Vault (every VAULT_EVERY runs: pick 1 of 3 resource bundles)
// ============================================================================

/** The three vault options for a window (crafting.md §2.4). */
export function vaultOffer(windowLevels: readonly number[], tier: RankTier): VaultOffer {
  const bestLevel = windowLevels.length ? Math.max(...windowLevels) : 2;
  const grade = gradeForKey(bestLevel);
  const options: VaultOption[] = [
    { kind: 'scales', bundle: { fangsteel: VAULT_SCALE_CACHE.fangsteel, scales: { [grade]: VAULT_SCALE_CACHE.scales } } },
    { kind: 'token', bundle: {}, token: { grade } },
    {
      kind: 'altar',
      bundle: {
        sigils: VAULT_ALTAR_CACHE.sigils,
        venom: VAULT_ALTAR_CACHE.venom,
        moltskin: VAULT_ALTAR_CACHE.moltskin,
        ...(atLeast(tier, 'champion') ? { eyeFragments: VAULT_ALTAR_CACHE.eyeFragmentsChampion } : {}),
      },
    },
  ];
  return { grade, bestLevel, options };
}

/** Bundle of a vault option (the token option's bundle is empty: it grants a Vault Forge Token). */
export const vaultOptionBundle = (o: VaultOption): ResourceBundle => addBundles(o.bundle);

/** Companion payout: classes in the party other than the human. */
export function satchelRecipients(party: readonly { classId: ClassId; controller: 'human' | 'bot' }[]): ClassId[] {
  return [...new Set(party.filter((m) => m.controller === 'bot').map((m) => m.classId))];
}

// ============================================================================
// +world-resources: world finds (breakables, hidden caches, reagent nodes)
// ============================================================================

/**
 * The sim's world finds as a wallet bundle (docs/design/world-resources.md §4): 'scale' is the
 * key's Coil Scale grade, 'scaleUp' one grade above (capped at Eternal). Gold goes to gold.
 */
export function worldFindsToBundle(finds: WorldFinds | null | undefined, level: number): ResourceBundle {
  const out: ResourceBundle = {};
  if (!finds) return out;
  const n = (v: number | undefined): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
  const grade = gradeForKey(level);
  const up = SCALE_GRADES[Math.min(SCALE_GRADES.length - 1, gradeIndex(grade) + 1)]!;
  if (n(finds.fangsteel)) out.fangsteel = n(finds.fangsteel);
  if (n(finds.venom)) out.venom = n(finds.venom);
  if (n(finds.moltskin)) out.moltskin = n(finds.moltskin);
  if (n(finds.eyeFragment)) out.eyeFragments = n(finds.eyeFragment);
  if (n(finds.gold)) out.gold = n(finds.gold);
  const scales: Partial<Record<ScaleGrade, number>> = {};
  if (n(finds.scale)) scales[grade] = n(finds.scale);
  if (n(finds.scaleUp)) scales[up] = (scales[up] ?? 0) + n(finds.scaleUp);
  if (Object.keys(scales).length) out.scales = scales;
  return out;
}
